// apps/admin/src/features/products/ApprovalDiffModal.tsx
//
// Phase 8.2 Wave 6 Task 41 — Side-by-side re-approval diff modal.
//
// vg-binding: PLAN:task-41
// vg-binding: CONTEXT:decisions=P8.D-188,P8.D-216,P8.D-309,P8.D-310,P8.D-311
// vg-binding: TEST-GOALS:G-33
//
// Per CONTEXT.md (5 trigger fields: processingTime / baseCost
// capacityPerDay / shippingZones / productionSpecs) + (re-approval
// rows show diff icon → modal side-by-side diff). Backend (Task 30,
// product-approval.diff.ts) returns the diff array structured as
// `{ field, before, after }` — this modal renders one row per entry.
//
// Renders the unified "no relevant changes" empty state when the entry list
// is empty (vendor edited only non-trigger fields like description/images/
// tags — those MUST NOT trigger re-approval).

import type { JSX } from 'react';

import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  formatUsdCents,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API-CONTRACTS DiffEntry returned by
// product-approval.diff.ts. The 5 field literals match
// RE_APPROVAL_TRIGGER_FIELDS verbatim — adding here is a CONTEXT amend.
// ---------------------------------------------------------------------------

export type ReApprovalTriggerField =
  | 'processingTime'
  | 'baseCost'
  | 'capacityPerDay'
  | 'shippingZones'
  | 'productionSpecs';

// dbg #436 — vendor self-submit re-approvals diff a different field set
// (cost_price_cents + per-variant dimensions / processing_time_days /
// capacity_per_day / weight_grams, keyed `variant_<field>`) than the admin
// Excel path. `field` is therefore widened to `string`; the modal falls back
// to the raw key when no locale label exists. `context` carries the variant
// identity (size / color) as business data — rendered verbatim, not translated.
export interface ApprovalDiffEntry {
  field: ReApprovalTriggerField | string;
  before: unknown;
  after: unknown;
  context?: string;
}

/**
 * 2026-10-13 — a vendor product imported against Printway SKUs: the blueprint
 * it links to and, per SKU, vendor cost vs the lowest merchant tier (admin
 * only). Approving creates the vendor ↔ blueprint link for these SKUs.
 */
export interface ApprovalMapping {
  blueprint_id: string;
  blueprint_name: string;
  printway_labels: Record<string, { size: string; color: string }>;
  skus: Array<{
    variant_id: string;
    sku: string;
    vendor_size: string;
    vendor_color: string;
    enabled: boolean;
    cost_cents: number;
    lowest_tier_cents: number | null;
    margin_pct: number | null;
    violation: boolean;
  }>;
}

export interface ApprovalDiffModalProps {
  open: boolean;
  onClose: () => void;
  /** Vendor + product context surfaced in the modal header. */
  vendorName?: string | null;
  productName?: string | null;
  /** Diff rows from `product-approval.diff.ts::diffTriggerFields`. */
  entries: ApprovalDiffEntry[];
  /** Optional approve / reject buttons rendered in the footer. */
  onApprove?: () => void;
  onReject?: () => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  mapping?: ApprovalMapping | null;
}

// ---------------------------------------------------------------------------
// Helpers — render arbitrary trigger-field values uniformly
// ---------------------------------------------------------------------------

/** CN1-R1-02 — money fields of the diff (`*_cents`) render as USD. */
function isCentsField(field: string): boolean {
  return field.endsWith('_cents');
}

function renderValue(value: unknown, field = ''): string {
  if (value === null || value === undefined) return '—';
  if (isCentsField(field) && typeof value === 'number' && Number.isFinite(value)) {
    return formatUsdCents(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return value
      .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ApprovalDiffModal({
  open,
  onClose,
  vendorName,
  productName,
  entries,
  onApprove,
  onReject,
  approveLoading,
  rejectLoading,
  mapping,
}: ApprovalDiffModalProps): JSX.Element | null {
  const { t } = useTranslation('catalog');
  if (!open) return null;

  const headerSuffix =
    vendorName || productName
      ? ` — ${[vendorName, productName].filter(Boolean).join(' · ')}`
      : '';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      data-testid="product-approval-diff-modal"
    >
      <DialogHeader>
        <DialogTitle>
          {t('product_approval.diffModal.title')}
          {headerSuffix}
        </DialogTitle>
      </DialogHeader>

      {mapping ? (
        <div className="px-6 pb-4" data-testid="product-approval-mapping">
          <p className="mb-2 text-sm text-pw-text">
            {t('product_approval.mapping.linksTo')}{' '}
            <span className="font-semibold" data-testid="product-approval-mapping-blueprint">
              {mapping.blueprint_name || mapping.blueprint_id}
            </span>
          </p>
          <div className="overflow-x-auto rounded-md border border-pw-border">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-pw-border bg-pw-surface text-left text-pw-text-muted">
                  <th className="px-3 py-2">{t('product_approval.mapping.col.sku')}</th>
                  <th className="px-3 py-2">{t('product_approval.mapping.col.vendorLabel')}</th>
                  <th className="px-3 py-2">{t('product_approval.mapping.col.printwayLabel')}</th>
                  <th className="px-3 py-2 text-right">{t('product_approval.mapping.col.cost')}</th>
                  <th className="px-3 py-2 text-right">{t('product_approval.mapping.col.lowestTier')}</th>
                  <th className="px-3 py-2 text-right">{t('product_approval.mapping.col.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {mapping.skus.map((s) => {
                  const pw = mapping.printway_labels[s.sku];
                  const vendorLabel = `${s.vendor_size} / ${s.vendor_color}`;
                  const pwLabel = pw ? [pw.size, pw.color].filter(Boolean).join(' / ') : '—';
                  const mismatch =
                    !!pw &&
                    ((pw.size && pw.size.toLowerCase() !== s.vendor_size.toLowerCase()) ||
                      (pw.color && pw.color.toLowerCase() !== s.vendor_color.toLowerCase()));
                  return (
                    <tr
                      key={s.variant_id}
                      className={cn('border-b border-pw-border last:border-b-0', s.violation && 'bg-red-500/5')}
                      data-testid={`product-approval-mapping-row-${s.sku}`}
                      data-violation={s.violation ? 'true' : 'false'}
                    >
                      <td className="px-3 py-2 font-mono text-pw-text">
                        {s.sku}
                        {!s.enabled ? (
                          <span className="ml-1 text-pw-text-muted">({t('product_approval.mapping.disabled')})</span>
                        ) : null}
                      </td>
                      <td className={cn('px-3 py-2', mismatch ? 'text-amber-700 dark:text-amber-300' : 'text-pw-text')}>
                        {vendorLabel}
                        {mismatch ? <span className="sr-only"> — {t('product_approval.mapping.labelMismatch')}</span> : null}
                      </td>
                      <td className="px-3 py-2 text-pw-text-muted">{pwLabel}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatUsdCents(s.cost_cents)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {s.lowest_tier_cents == null ? '—' : formatUsdCents(s.lowest_tier_cents)}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          s.violation ? 'font-semibold text-red-700 dark:text-red-300' : 'text-pw-text',
                        )}
                      >
                        {s.margin_pct == null
                          ? t('product_approval.mapping.noPrice')
                          : `${s.margin_pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {mapping.skus.some((s) => s.violation) ? (
            <p className="mt-2 text-xs text-red-700 dark:text-red-300" data-testid="product-approval-mapping-violation">
              {t('product_approval.mapping.violationHint')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="px-6 pb-4">
        {entries.length === 0 ? (
          // A first submission has no diff; its mapping table above is the content.
          mapping ? null : (
            <p className="text-sm text-pw-text-muted py-8 text-center">
              {t('product_approval.diffModal.empty')}
            </p>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-pw-border bg-pw-surface">
                  <th className="py-2 px-3 text-left font-medium text-pw-text-muted w-1/4">
                    {t('product_approval.diffModal.col.field')}
                  </th>
                  <th className="py-2 px-3 text-left font-medium text-pw-text-muted">
                    {t('product_approval.diffModal.col.previous')}
                  </th>
                  <th className="py-2 px-3 text-left font-medium text-pw-text-muted">
                    {t('product_approval.diffModal.col.new')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={`${entry.field}${entry.context ? `-${entry.context}` : ''}`}
                    className="border-b border-pw-border align-top"
                    data-testid={`product-approval-diff-row-${entry.field}`}
                  >
                    <td className="py-3 px-3 font-medium text-pw-text">
                      {/* dbg #436 — vendor snapshot fields (variant_*) may lack a
                          locale label; defaultValue falls back to the raw key so
                          the row still renders instead of showing a bare i18n key. */}
                      {t(`product_approval.diffModal.fields.${entry.field}`, {
                        defaultValue: entry.field,
                      })}
                      {entry.context ? (
                        <span className="ml-1 block text-xs font-normal text-pw-text-muted">
                          {entry.context}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 px-3 text-rose-700 dark:text-rose-300">
                      <pre className="whitespace-pre-wrap break-all font-mono text-xs">
                        {renderValue(entry.before, entry.field)}
                      </pre>
                    </td>
                    <td className="py-3 px-3 text-emerald-700 dark:text-emerald-300">
                      <pre className="whitespace-pre-wrap break-all font-mono text-xs">
                        {renderValue(entry.after, entry.field)}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t('product_approval.diffModal.actions.close')}
        </Button>
        {onReject ? (
          <Button
            variant="destructive"
            onClick={onReject}
            disabled={rejectLoading}
          >
            {rejectLoading
              ? t('product_approval.reject.submitting')
              : t('product_approval.actions.reject')}
          </Button>
        ) : null}
        {onApprove ? (
          <Button onClick={onApprove} disabled={approveLoading}>
            {approveLoading
              ? t('product_approval.bulk.approving')
              : t('product_approval.actions.approve')}
          </Button>
        ) : null}
      </DialogFooter>
    </Dialog>
  );
}

export default ApprovalDiffModal;
